import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MemberService } from '../member-service/MemberService ';
import { ActivatedRoute } from '@angular/router';

// Meeting Note interface (matching API response)
interface MeetingNote {
  id?: string;
  memberId?: string;
  createdBy?: string;
  lastModifiedBy?: string;
  meetingDate: string;
  meetingTitle?: string;
  attendees: string[]; // Array from API
  keyTopics: string[];
  lastCallSummary: string;
  actionItems: string[];
  createdDate?: string;
  modifiedDate?: string;
  isEditing?: boolean;
  hasChanges?: boolean;
}

// UI model for display
interface MeetingNoteUI {
  id?: string;
  date: string;
  heading: string;
  createdBy?: string;
  createdDate?: string;
  attendees: string; // Comma-separated string for UI
  keyTopics: string[];
  notes: string;
  actionItems: string[];
  rawData?: MeetingNote; // Store original API data
  isEditing?: boolean;
  hasChanges?: boolean;
}

@Component({
  selector: 'app-meeting-notes',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './meeting-notes.component.html',
  styleUrls: ['./meeting-notes.component.css']
})
export class MeetingNotesComponent {
  memberId: string = '';
  
  // New meeting note form
  newMeetingNote: MeetingNoteUI = {
    date: '',
    heading: '',
    attendees: '',
    keyTopics: [],
    notes: '',
    actionItems: [''],
    hasChanges: false
  };
  
  // Temporary inputs for adding items
  newKeyTopic: string = '';
  
  // Previous meeting notes
  previousMeetingNotes: MeetingNoteUI[] = [];
  
  // Temporary inputs for each card
  tempInputs: Map<string, {keyTopic: string}> = new Map();
  
  // Date picker search
  searchDate: string = '';
  
  // Maximum date for meeting date field (today's date)
  maxDate: string = '';

  constructor(
    private memberService: MemberService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    // Set max date to today for meeting date field
    const today = new Date();
    this.maxDate = today.toISOString().split('T')[0];
    
    // Get memberId from route
    this.memberId = this.route.snapshot.queryParamMap.get('id') || '';
    
    // Load meeting notes
    if (this.memberId) {
      this.loadMeetingNotes();
    }
  }

  loadMeetingNotes(): void {
    this.memberService.getMeetingNotesByMemberId(this.memberId).subscribe({
      next: (response) => {
        if (response && response.result) {
          this.previousMeetingNotes = this.mapApiDataToUI(response.result);
          
          // Initialize temp inputs for each card
          this.previousMeetingNotes.forEach(note => {
            if (note.id) {
              this.tempInputs.set(note.id, {keyTopic: ''});
            }
          });
        }
      },
      error: (error) => {
        console.error('Error loading meeting notes:', error);
        // Show empty state or error message
        this.previousMeetingNotes = [];
      }
    });
  }

  // Map API data to UI model
  mapApiDataToUI(apiData: MeetingNote[]): MeetingNoteUI[] {
    return apiData.map(note => ({
      id: note.id,
      date: note.meetingDate ? note.meetingDate.split('T')[0] : '',
      createdBy: note.createdBy || note.lastModifiedBy || '',
      createdDate: note.createdDate || '',
      heading: note.meetingTitle || '',
      attendees: note.attendees ? note.attendees.join(', ') : '',
      keyTopics: note.keyTopics || [],
      notes: note.lastCallSummary || '',
      actionItems: note.actionItems || [],
      rawData: note,
      isEditing: false,
      hasChanges: false
    }));
  }

  // Map UI data to API model
  mapUIDataToApi(uiNote: MeetingNoteUI): any {
    return {
      memberId: this.memberId,
      meetingDate: uiNote.date ? new Date(uiNote.date).toISOString() : new Date().toISOString(),
      meetingTitle: uiNote.heading,
      attendees: uiNote.attendees.split(',').map(a => a.trim()).filter(a => a),
      keyTopics: uiNote.keyTopics,
      lastCallSummary: uiNote.notes,
      actionItems: uiNote.actionItems.filter(item => item && item.trim()),
      createdDate: uiNote.rawData?.createdDate || new Date().toISOString(),
      modifiedDate: new Date().toISOString()
    };
  }

  // New Meeting Note Methods
  addKeyTopicToNew(): void {
    if (this.newKeyTopic.trim()) {
      this.newMeetingNote.keyTopics.push(this.newKeyTopic.trim());
      this.newKeyTopic = '';
      this.markNewNoteChanged();
    }
  }

  removeKeyTopicFromNew(index: number): void {
    this.newMeetingNote.keyTopics.splice(index, 1);
    this.markNewNoteChanged();
  }

  addActionItemToNew(): void {
    this.newMeetingNote.actionItems.push('');
    this.markNewNoteChanged();
  }

  removeActionItemFromNew(index: number): void {
    this.newMeetingNote.actionItems.splice(index, 1);
    this.markNewNoteChanged();
  }

  markNewNoteChanged(): void {
    this.newMeetingNote.hasChanges = true;
  }

  saveNewMeetingNote(): void {
    console.log('Saving new meeting note:', this.newMeetingNote);
    
    if (!this.newMeetingNote.date) {
      alert('Please select a date for the meeting note');
      return;
    }
    
    const apiData = this.mapUIDataToApi(this.newMeetingNote);
    
    this.memberService.saveMeetingNote(apiData).subscribe({
      next: (response) => {
        console.log('Meeting note saved successfully:', response);
        alert('Meeting note saved successfully!');
        
        // Reload meeting notes
        this.loadMeetingNotes();
        
        // Reset form
        this.resetNewMeetingNote();
      },
      error: (error) => {
        console.error('Error saving meeting note:', error);
        alert('Failed to save meeting note. Please try again.');
      }
    });
  }

  resetNewMeetingNote(): void {
    this.newMeetingNote = {
      date: '',
      heading: '',
      attendees: '',
      keyTopics: [],
      notes: '',
      actionItems: [''],
      hasChanges: false
    };
    this.newKeyTopic = '';
  }

  // Previous Meeting Notes Methods
  enableEdit(note: MeetingNoteUI): void {
    note.isEditing = true;
  }

  cancelEdit(note: MeetingNoteUI): void {
    note.isEditing = false;
    note.hasChanges = false;
    // Reload data to discard changes
    this.loadMeetingNotes();
  }

  markNoteChanged(note: MeetingNoteUI): void {
    note.hasChanges = true;
  }

  saveChanges(note: MeetingNoteUI): void {
    console.log('Saving changes for note:', note);
    
    // Search for matching record by meetingDate or meetingTitle
    const matchingNote = this.findMatchingNote(note);
    
    const apiData = this.mapUIDataToApi(note);
    
    if (matchingNote && matchingNote.id) {
      // Match found - update existing record
      console.log('Match found, updating existing note with ID:', matchingNote.id);
      
      this.memberService.updateMeetingNote(matchingNote.id, apiData).subscribe({
        next: (response) => {
          console.log('Meeting note updated successfully:', response);
          alert('Changes saved successfully!');
          
          note.isEditing = false;
          note.hasChanges = false;
          
          // Reload to get fresh data
          this.loadMeetingNotes();
        },
        error: (error) => {
          console.error('Error updating meeting note:', error);
          alert('Failed to save changes. Please try again.');
        }
      });
    } else {
      // No match found - create new record
      console.log('No match found, creating new meeting note');
      
      this.memberService.saveMeetingNote(apiData).subscribe({
        next: (response) => {
          console.log('New meeting note created successfully:', response);
          alert('New meeting note created successfully!');
          
          note.isEditing = false;
          note.hasChanges = false;
          
          // Reload to get fresh data
          this.loadMeetingNotes();
        },
        error: (error) => {
          console.error('Error creating meeting note:', error);
          alert('Failed to create meeting note. Please try again.');
        }
      });
    }
  }

  deleteNote(note: MeetingNoteUI): void {
    if (!confirm('Are you sure you want to delete this meeting note?')) {
      return;
    }
    
    // Search for matching record by meetingDate or meetingTitle
    const matchingNote = this.findMatchingNote(note);
    
    if (!matchingNote || !matchingNote.id) {
      alert('Cannot find matching record to delete');
      return;
    }
    
    console.log('Match found for deletion, using ID:', matchingNote.id);
    
    this.memberService.deleteMeetingNote(matchingNote.id).subscribe({
      next: (response) => {
        console.log('Meeting note deleted successfully:', response);
        alert('Meeting note deleted successfully!');
        
        // Remove from UI
        const index = this.previousMeetingNotes.findIndex(n => n.id === matchingNote.id);
        if (index > -1) {
          this.previousMeetingNotes.splice(index, 1);
          if (matchingNote.id) {
            this.tempInputs.delete(matchingNote.id);
          }
        }
      },
      error: (error) => {
        console.error('Error deleting meeting note:', error);
        alert('Failed to delete meeting note. Please try again.');
      }
    });
  }

  // Previous notes - key topics
  addKeyTopicToNote(note: MeetingNoteUI): void {
    if (!note.id) return;
    
    const tempInput = this.tempInputs.get(note.id);
    if (tempInput && tempInput.keyTopic.trim()) {
      note.keyTopics.push(tempInput.keyTopic.trim());
      tempInput.keyTopic = '';
      this.markNoteChanged(note);
    }
  }

  removeKeyTopicFromNote(note: MeetingNoteUI, index: number): void {
    note.keyTopics.splice(index, 1);
    this.markNoteChanged(note);
  }

  // Previous notes - action items
  addActionItemToNote(note: MeetingNoteUI): void {
    note.actionItems.push('');
    this.markNoteChanged(note);
  }

  removeActionItemFromNote(note: MeetingNoteUI, index: number): void {
    note.actionItems.splice(index, 1);
    this.markNoteChanged(note);
  }

  // Date formatting
  formatDate(dateString: string): string {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long', year: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  }

  // Search by date
  searchByDate(): void {
    if (!this.searchDate) {
      this.loadMeetingNotes();
      return;
    }
    
    console.log('Searching notes for date:', this.searchDate);
    // TODO: Implement API call to filter by date
    
    // For now, filter locally
    const allNotes = [...this.previousMeetingNotes];
    this.previousMeetingNotes = allNotes.filter(note => note.date === this.searchDate);
  }

  clearSearch(): void {
    this.searchDate = '';
    this.loadMeetingNotes();
  }

  // Helper to get temp input for a note
  getTempInput(noteId: string | undefined): {keyTopic: string} {
    if (!noteId) return {keyTopic: ''};
    return this.tempInputs.get(noteId) || {keyTopic: ''};
  }

  // TrackBy function to prevent focus loss in ngFor
  trackByIndex(index: number, item: any): number {
    return index;
  }

  // Find matching note by meetingDate or meetingTitle
  findMatchingNote(note: MeetingNoteUI): MeetingNoteUI | undefined {
    // First, try to use the stored rawData ID if available
    if (note.rawData && note.rawData.id) {
      const exactMatch = this.previousMeetingNotes.find(n => n.rawData?.id === note.rawData?.id);
      if (exactMatch) {
        return exactMatch;
      }
    }
    
    // If no rawData ID or exact match not found, search by date and/or title
    return this.previousMeetingNotes.find(n => {
      // Match by both date and title if both exist
      const dateMatch = n.date && note.date && n.date === note.date;
      const titleMatch = n.heading && note.heading && 
                         n.heading.toLowerCase().trim() === note.heading.toLowerCase().trim();
      
      // Match if either:
      // 1. Both date and title match
      // 2. Only date matches (if title is empty or not provided)
      // 3. Only title matches (if date is empty or not provided)
      if (dateMatch && titleMatch) {
        return true; // Both match - strongest match
      } else if (dateMatch && (!n.heading || !note.heading)) {
        return true; // Date matches and title not provided
      } else if (titleMatch && (!n.date || !note.date)) {
        return true; // Title matches and date not provided
      }
      
      return false;
    });
  }
}

